import { useCallback, useRef } from 'react';

interface QualityConfig {
  enableAdaptation?: boolean;
  selectedQuality?: number;
  availableQualities?: Array<{
    height: number;
    bandwidth: number;
    label: string;
  }>;
}

interface Quality {
  height: number;
  bandwidth: number;
  label: string;
}

export const useQualityControl = (
  playerRef: React.RefObject<any>,
  qualityConfig?: QualityConfig,
  onQualityChange?: (quality: { height: number; bandwidth: number }) => void
) => {
  const getAvailableQualities = useCallback((): Quality[] => {
    if (!playerRef.current) return [];
    
    const tracks = playerRef.current.getVariantTracks();
    const qualities = tracks
      .filter((track: any, index: any, self: any) => 
        index === self.findIndex((t: any) => t.height === track.height)
      )
      .map((track: any) => ({
        height: track.height || 0,
        bandwidth: track.bandwidth || 0,
        label: `${track.height}p`
      }))
      .sort((a: any, b: any) => b.height - a.height);
    
    return qualities;
  }, [playerRef]);

  const setQuality = useCallback((height: number) => {
    if (!playerRef.current) return;
    
    if (height === 0) {
      // Enable adaptive streaming
      playerRef.current.configure({
        abr: { enabled: true, 
          switchInterval: 2,     // quicker re-checks
          clearBufferSwitch: true,
          safeMarginSwitch: 10   // leave ~10 s in front of the playhead }
        }
      })
    } else {
      // Disable adaptive streaming and select specific quality
      playerRef.current.configure({
        abr: { enabled: false,
          switchInterval: 2,     // quicker re-checks
          clearBufferSwitch: true,
          safeMarginSwitch: 10   // leave ~10 s in front of the playhead }  
        }
      });
      
      const tracks = playerRef.current.getVariantTracks();
      const targetTrack = tracks.find((track: any) => track.height === height);
      if (targetTrack) {
        playerRef.current.selectVariantTrack(targetTrack, /* clearBuffer= */ true);
      }
    }
  }, [playerRef]);

  const setupQualityTracking = useCallback(() => {
    if (!playerRef.current || !onQualityChange) return;

    const handleQualityChange = () => {
      const activeTrack = playerRef.current.getVariantTracks().find((track: any) => track.active);
      if (activeTrack) {
        onQualityChange({
          height: activeTrack.height || 0,
          bandwidth: activeTrack.bandwidth || 0
        });
      }
    };

    playerRef.current.addEventListener('variantchanged', handleQualityChange);
    
    return () => {
      playerRef.current?.removeEventListener('variantchanged', handleQualityChange);
    };
  }, [playerRef, onQualityChange]);

  const configureQuality = useCallback(() => {
    if (!playerRef.current || !qualityConfig) return;

    if (qualityConfig.enableAdaptation !== undefined) {
      playerRef.current.configure({
        abr: { enabled: qualityConfig.enableAdaptation }
      });
    }

    if (qualityConfig.selectedQuality !== undefined) {
      setQuality(qualityConfig.selectedQuality);
    }
  }, [playerRef, qualityConfig, setQuality]);

  return {
    getAvailableQualities,
    setQuality,
    setupQualityTracking,
    configureQuality
  };
}; 